import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Field } from '../components/ui';

export function ChangePasswordPage() {
  const { t } = useTranslation();
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);

  // Leave only once the refreshed user is in the auth state (and therefore in the router's guards).
  useEffect(() => {
    if (changed && me && !me.must_change_password) void navigate({ to: '/' });
  }, [changed, me, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next.length < 10) return setError(t('password.tooShort'));
    if (next !== confirm) return setError(t('password.mismatch'));
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/me/password', { current_password: current, new_password: next });
      toast.success(t('password.changed'));
      await refresh();
      setChanged(true);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? t('password.wrongCurrent') : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <form onSubmit={submit} className="card space-y-3 p-6">
        <h1 className="text-lg font-semibold">{t('password.changeTitle')}</h1>
        {me?.must_change_password && <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">{t('password.changeRequired')}</p>}
        <Field label={t('password.current')} required>
          <input type="password" className="input" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        </Field>
        <Field label={t('password.new')} required hint={t('password.tooShort')}>
          <input type="password" className="input" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required minLength={10} />
        </Field>
        <Field label={t('password.confirmNew')} required>
          <input type="password" className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
        </Field>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {t('app.save')}
        </button>
      </form>
    </div>
  );
}
